/*
 * 地磅连接日志上报（前端埋点版）。
 *
 * [2026-09-15] 运营需要每周统计各厂地磅（UPort 串口）连接稳定性。采集点放在烘干厂 PC
 * 前端而不是 jinglunGo：用户必须打开本系统才能用地磅，网页打开期间的连接事件已覆盖
 * 全部使用场景；且网页是集中发布的，现场不用更新好粮宝/jinglunGo 就能开始上报。
 *
 * 采集什么（打开系统期间的地磅连接全生命周期）：
 *   session_start  进入系统（全局地磅栏挂载）
 *   connect_ok     地磅连接成功（数据门禁口径：首帧有效读数，与页面亮灯同一时刻）
 *   connect_fail   连接尝试失败（被占用/拔线/设备故障/外设服务未启动/收不到数据...）
 *   disconnect     连接断开（手动断开 / 读错误拔线 / WS status connected=false）
 *   session_end    离开系统（刷新/关页/关机，beforeunload 里 fetch keepalive 尽力补报）
 *
 * 埋点位置（谁改谁看）：
 *   - connect_ok / disconnect：store.subscribe 监听 SET_SCALE_CONNECTED（全局唯一状态出口，
 *     web/jinglun 双模式、自动/手动连接全都汇聚在这）
 *   - connect_fail：weighbridgeMixin 三处连接 catch（自动连接/手动智能连接/数据门禁失败）
 *   - 读错误原因：本模块订阅 ScaleStream 的 error 事件做暂存，断开事件就近归因
 *
 * 上报：fire-and-forget，2s 防抖小批量 POST /drying/scale-connect-log/report（带 token），
 * 失败静默丢弃（遥测数据，不重试不阻塞业务）。
 */

import request from '@/utils/request'
import { ScaleStream } from '@/utils/scaleClient'

const REPORT_URL = '/hl-drying/drying/scale-connect-log/report'
// beforeunload 的补报走裸 fetch (axios 不支持 keepalive), fetch 不吃 axios 的 baseURL,
// 必须自己拼上 /api 前缀, 否则生产 nginx 无 /hl-drying location → POST 405, session_end 全丢.
const REPORT_FETCH_URL = (request.defaults.baseURL || '') + REPORT_URL
const DEVICE_KEY = 'drying_scale_log_device_v1'
const FLUSH_DELAY_MS = 2000
const FLUSH_MAX = 20
const ERR_STASH_MS = 60000
const OVERRIDE_STASH_MS = 10000

let initialized = false
let storeRef = null
let sessionId = ''
let deviceId = ''
let pending = []
let flushTimer = null

// 每个 磅(scaleKey) 的会话内状态
//   seq               连接事件序号（connect_ok/connect_fail 各自递增，1=进入系统后的初始连接）
//   lastAttempt       最近一次连接尝试的串口维度（connect 事件补齐 port/baud/protocol 用）
//   lastError         最近一次 WS/连接错误（断开就近归因用，60s 内有效）
//   manualClose       手动断开标记（下一次 disconnect 归因为 manual_close）
//   nextDisconnect    一次性断开归因覆盖（手动断开/web 切页收口等明确动作，10s 内有效）
//   reportedConnected 上报闸门：本会话内该磅上次上报的连接状态（true=已上报"已连接"）。
//     [2026-09-16] SET_SCALE_CONNECTED 存在大量重复提交（web 模式旧版每帧提交、对账/
//     设备配置页无条件提交），只有状态相对上次上报真实翻转才记事件，否则一天能刷出
//     近十万条 connect_ok。
const scaleState = {}

function stateOf(scaleKey) {
  if (!scaleState[scaleKey]) {
    scaleState[scaleKey] = {
      seq: 0, lastAttempt: null, lastError: null, manualClose: false,
      nextDisconnect: null, reportedConnected: false,
    }
  }
  return scaleState[scaleKey]
}

function currentFactoryId() {
  try {
    const s = (storeRef && storeRef.state) || {}
    return (s.externalOrg && s.externalOrg.saleOrgId) || (s.loginUser && s.loginUser.externalOrgId) || ''
  } catch (e) { return '' }
}

function ensureDeviceId() {
  try {
    let v = localStorage.getItem(DEVICE_KEY)
    if (!v) {
      v = 'dev-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
      localStorage.setItem(DEVICE_KEY, v)
    }
    return v
  } catch (e) { return 'dev-unknown' }
}

function newId(prefix) {
  return prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
}

/** 错误归因：错误码/文案 → 原因码（关键词与 jinglunGo friendlyScaleError / 前端
 *  friendlySerialError 的中文文案对齐） */
function classifyError(code, message) {
  const s = `${code || ''} ${message || ''}`
  if (code === 'SERVICE_UNAVAILABLE' || s.indexOf('外设服务') >= 0 ||
      s.indexOf('Network Error') >= 0 || s.indexOf('ECONN') >= 0) return 'service_down'
  // [2026-09-16] web 模式数据超时：有原始字节但解析不出有效帧 = 波特率/协议不对；
  // 完全无数据 = 断电/拔线/线松
  if (code === 'DATA_TIMEOUT_PARSED') return 'baud_mismatch'
  if (code === 'DATA_TIMEOUT_SILENT') return 'no_data'
  if (s.indexOf('占用') >= 0 || s.indexOf('in use') >= 0 || s.indexOf('busy') >= 0 ||
      s.indexOf('access denied') >= 0 || s.indexOf('拒绝访问') >= 0) return 'port_occupied'
  if (s.indexOf('找不到') >= 0 || s.indexOf('不存在') >= 0 || s.indexOf('not found') >= 0 ||
      s.indexOf('has been lost') >= 0 || s.indexOf('已丢失') >= 0 || s.indexOf('拔出') >= 0) return 'device_unplugged'
  if (s.indexOf('设备异常') >= 0 || s.indexOf('USB') >= 0 || s.indexOf('驱动') >= 0) return 'device_error'
  if (s.indexOf('读取数据时出错') >= 0 || s.indexOf('读取错误') >= 0) return 'read_error'
  if (s.indexOf('超时') >= 0) return 'read_error'
  if (s.indexOf('收不到地磅数据') >= 0 || s.indexOf('收不到读数') >= 0 ||
      s.indexOf('收不到串口数据') >= 0 || s.indexOf('收不到数据') >= 0) return 'no_data'
  return 'unknown'
}

function pushEvent(eventType, scaleKey, fields) {
  const st = scaleKey ? stateOf(scaleKey) : {}
  const ev = Object.assign({
    eventType,
    scaleKey: scaleKey || '',
    attemptSeq: 0,
    eventTime: Date.now()
  }, fields || {})
  if (!ev.reasonText) ev.reasonText = ''
  ev.deviceId = deviceId
  ev.factoryId = currentFactoryId()
  ev.sessionId = sessionId
  ev.version = (process.env && process.env.VUE_APP_VERSION) || ''
  if (st.lastAttempt) {
    ev.port = ev.port || st.lastAttempt.port || ''
    ev.baudRate = ev.baudRate || st.lastAttempt.baudRate || 0
    ev.protocol = ev.protocol || st.lastAttempt.protocol || ''
  }
  pending.push(ev)
  if (pending.length >= FLUSH_MAX) {
    flush()
  } else {
    scheduleFlush()
  }
}

function scheduleFlush() {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_DELAY_MS)
}

function flush() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null }
  if (!pending.length) return
  const batch = pending.splice(0, pending.length)
  request.post(REPORT_URL, {
    deviceId,
    factoryId: currentFactoryId(),
    sessionId,
    events: batch
  }).catch(() => { /* 遥测 fire-and-forget：失败静默丢弃，不重试不提示 */ })
}

// ===== 对外埋点 API（weighbridgeMixin / 页面调用）=====

/**
 * 初始化：进入系统时调用（全局地磅栏 mounted）。
 * 记 session_start + 订阅全局连接状态 + 注册离开页补报。
 * @param {Object} store Vuex store 实例（传进来避免模块循环依赖）
 */
export function initScaleConnectLog(store) {
  if (initialized || !store) return
  initialized = true
  storeRef = store
  deviceId = ensureDeviceId()
  sessionId = newId('pg')

  // 连接状态全局出口：SET_SCALE_CONNECTED 是 web/jinglun、自动/手动所有路径的汇聚点。
  // [2026-09-16] 状态闸门：该 mutation 有大量语义重复的提交（web 模式旧版每帧提交、
  // 状态对账/设备配置页无条件提交），只有连接状态相对上次上报真实翻转才记事件。
  store.subscribe((mutation) => {
    if (!mutation || mutation.type !== 'SET_SCALE_CONNECTED') return
    const payload = mutation.payload || {}
    const scaleKey = normalizeScaleKey(payload.scale)
    if (!scaleKey) return
    const st = stateOf(scaleKey)
    if (payload.connected) {
      if (st.reportedConnected) return // 已是"已连接"，重复提交不记
      st.reportedConnected = true
      pushEvent('connect_ok', scaleKey, { attemptSeq: nextSeq(scaleKey) })
    } else {
      const wasConnected = st.reportedConnected
      st.reportedConnected = false
      if (!wasConnected) { // 本来就没连上，对账式 false 不记
        st.manualClose = false
        st.nextDisconnect = null
        return
      }
      let reasonCode = 'unknown'
      let reasonText = ''
      const ov = st.nextDisconnect
      if (ov && Date.now() - ov.at <= OVERRIDE_STASH_MS) {
        reasonCode = ov.code
        reasonText = ov.text || ''
      } else if (st.manualClose) {
        reasonCode = 'manual_close'
        reasonText = '用户在页面上手动断开'
      } else if (st.lastError && Date.now() - st.lastError.at <= ERR_STASH_MS) {
        reasonCode = classifyError(st.lastError.code, st.lastError.message)
        reasonText = st.lastError.message || ''
      } else {
        reasonText = '连接状态翻转为断开（60s 内无就近错误可归因）'
      }
      st.manualClose = false
      st.nextDisconnect = null
      pushEvent('disconnect', scaleKey, { attemptSeq: 0, reasonCode, reasonText })
    }
  })

  // 读错误就近归因：WS error 事件先暂存，等 disconnect 到来时归因
  ScaleStream.subscribe(null, null, (msg) => {
    if (!msg || !msg.scaleKey) return
    const st = stateOf(normalizeScaleKey(msg.scaleKey))
    st.lastError = { code: msg.code || '', message: msg.message || '', at: Date.now() }
  })

  // 离开系统补报：刷新/关页/关机都走 beforeunload（浏览器层面无法区分关页与关机，
  // 统一记 page_exit；这两者对稳定性统计都属正常动作，不影响指标）
  window.addEventListener('beforeunload', reportSessionEnd)

  pushEvent('session_start', '', {})
}

/** 连接尝试埋点：连接动作开始时调用（序号 +1，connect 事件携带） */
export function noteConnectAttempt(scaleKey, ctx) {
  const st = stateOf(normalizeScaleKey(scaleKey))
  st.seq += 1
  st.lastAttempt = Object.assign({}, ctx || {})
  st.lastError = null
  st.manualClose = false
  st.nextDisconnect = null
}

/** 连接失败埋点：连接 catch 时调用（err 为 scaleClient 归一化错误 {code,message}） */
export function noteConnectFail(scaleKey, err, ctx) {
  const key = normalizeScaleKey(scaleKey)
  const st = stateOf(key)
  if (ctx) st.lastAttempt = Object.assign({}, st.lastAttempt || {}, ctx)
  const reasonCode = classifyError(err && err.code, err && (err.message || err.code))
  pushEvent('connect_fail', key, {
    attemptSeq: st.seq || nextSeq(key),
    reasonCode,
    reasonText: (err && (err.message || err.code)) || ''
  })
}

/**
 * [2026-09-16] 读错误/数据超时暂存（web 模式用）：错误先记下，等断开事件到来时就近归因。
 * 与 jinglun 模式的 ScaleStream error 订阅等价 — web 模式的 WeighbridgeReader 错误
 * 此前到不了归因模块，是周报"未知"断线的主要来源。
 * code 传 DATA_TIMEOUT_PARSED / DATA_TIMEOUT_SILENT（数据超时带 rawDataReceived 区分），
 * 其余传可读的原始错误文案即可（classifyError 按关键词归因）。
 */
export function noteScaleError(scaleKey, code, message) {
  const st = stateOf(normalizeScaleKey(scaleKey))
  st.lastError = { code: code || '', message: message || '', at: Date.now() }
}

/** 手动断开埋点：disconnectScale 动作前调用（下一次 disconnect 归因为 manual_close） */
export function noteManualClose(scaleKey) {
  const st = stateOf(normalizeScaleKey(scaleKey))
  st.manualClose = true
  st.nextDisconnect = { code: 'manual_close', text: '用户在页面上手动断开', at: Date.now() }
}

/**
 * [2026-09-16] 明确断开动作的一次性归因覆盖（优先级高于就近错误暂存）：
 * web 模式切页/刷新/停用硬件时生命周期会主动关串口，这属正常动作而非不稳定，
 * 不标注会被计成"未知"断线污染周报。10s 内的下一次 disconnect 消费后失效。
 */
export function noteDisconnectReason(scaleKey, code, text) {
  const st = stateOf(normalizeScaleKey(scaleKey))
  st.nextDisconnect = { code: code || 'unknown', text: text || '', at: Date.now() }
}

/** 离开系统：beforeunload 时尽力补报 session_end（fetch keepalive 可带 token 头） */
export function reportSessionEnd() {
  if (!initialized) return
  const batch = pending.splice(0, pending.length)
  batch.push({
    eventType: 'session_end', scaleKey: '', attemptSeq: 0,
    reasonCode: 'page_exit', reasonText: '用户关闭/刷新页面或关机离开系统',
    deviceId, factoryId: currentFactoryId(), sessionId,
    version: (process.env && process.env.VUE_APP_VERSION) || '',
    eventTime: Date.now()
  })
  try {
    const token = localStorage.getItem('token')
    fetch(REPORT_FETCH_URL, {
      method: 'POST',
      keepalive: true,
      headers: Object.assign(
        { 'Content-Type': 'application/json' },
        token ? { 'X-Access-Token': token } : {}
      ),
      body: JSON.stringify({ deviceId, factoryId: currentFactoryId(), sessionId, events: batch })
    }).catch(() => {})
  } catch (e) { /* 尽力补报，失败即弃 */ }
}

// ===== 内部工具 =====

function normalizeScaleKey(scaleKey) {
  // store 用 entryScale/exitScale，jinglunGo WS 用 entry/exit — 统一成 entry/exit
  if (!scaleKey) return ''
  return String(scaleKey).replace(/Scale$/, '')
}

function nextSeq(scaleKey) {
  const st = stateOf(scaleKey)
  st.seq += 1
  return st.seq
}
